import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { FnfSettlement, InitiateFnfPayload, UpdateLedgerPayload } from './types';

export function useFnfList(status?: string) {
  return useQuery({
    queryKey: ['hr', 'fnf-list', status],
    queryFn: async () => {
      const params = status ? `?status=${encodeURIComponent(status)}` : '';
      const { data } = await api.get<{ settlements: FnfSettlement[] }>(`/api/hr/fnf${params}`);
      return data.settlements ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useFnfDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['hr', 'fnf-detail', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data } = await api.get<{ settlement: FnfSettlement }>(`/api/hr/fnf/${id}`);
      return data.settlement;
    },
  });
}

function invalidateFnf(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['hr', 'fnf-list'] });
  queryClient.invalidateQueries({ queryKey: ['hr', 'fnf-detail'] });
}

export function useInitiateFnf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InitiateFnfPayload) => {
      const { data } = await api.post<{ settlement: FnfSettlement }>('/api/hr/fnf', payload);
      return data.settlement;
    },
    onSuccess: () => invalidateFnf(queryClient),
  });
}

export function useUpdateLedger(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateLedgerPayload) => {
      const { data } = await api.patch<{ settlement: FnfSettlement }>(`/api/hr/fnf/${id}/ledger`, payload);
      return data.settlement;
    },
    onSuccess: () => invalidateFnf(queryClient),
  });
}

export function usePatchAssets(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetRecoveryId, recovered, notes }: { assetRecoveryId: string; recovered: boolean; notes?: string }) => {
      const { data } = await api.patch<{ settlement: FnfSettlement }>(`/api/hr/fnf/${id}/assets`, { assetRecoveryId, recovered, notes });
      return data.settlement;
    },
    onSuccess: () => invalidateFnf(queryClient),
  });
}

export function useSettleFnf(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notes?: string) => {
      const { data } = await api.patch<{ settlement: FnfSettlement }>(`/api/hr/fnf/${id}/settle`, { notes });
      return data.settlement;
    },
    onSuccess: () => invalidateFnf(queryClient),
  });
}
